import type { TournamentStaffDetail as TournamentRecord, TournamentListItem } from '@brush/api-client';
import { StatCard, TagPill } from '@brush/ui';
import { ArrowLeft, Edit2, FileUp, Upload } from 'lucide-react';
import { ExportControl } from '../ExportControl';
import { OverflowMenu, overflowMenuItem } from '../OverflowMenu';
import { resultRowKey, standingName } from '../../results/unclaimed';

/**
 * What the buy-in total is a total *of* — four states, because three of them are not the number.
 *
 * A room may record buy-ins for everybody, for nobody, or for some of the sheet, and the last is
 * the one that needs saying out loud: the figure above is then a lower bound and reads exactly like
 * a complete one. Naming the players it covers is also the more useful fact in the ordinary case,
 * since bullets over people is what tells an operator how much re-entry the event took.
 */
function buyinsHint(players: number, recorded: number): string {
  if (players === 0) return 'No results recorded yet';
  if (recorded === 0) return `Not recorded · ${players} player${players === 1 ? '' : 's'}`;
  if (recorded < players) return `Recorded for ${recorded} of ${players} players`;
  return `${players} player${players === 1 ? '' : 's'}`;
}

const medal = (position: number) =>
  position === 1
    ? 'bg-gold-500/20 text-ink-900'
    : position === 2
      ? 'bg-ink-800/10 text-ink-900'
      : position === 3
        ? 'bg-chip-500/15 text-ink-900'
        : 'bg-ink-800/5 text-ink-800/70';

export function TournamentDetail({
  tournament,
  full,
  sheet,
  platformTags,
  onEdit,
  onUploadResults,
  onImportResults,
  onBack,
}: {
  /** The row, from the page: everything at the top of the screen, there at once. */
  tournament: TournamentListItem;
  /**
   * The tournament's own record — its results and sittings, which the page no longer carries, each
   * result naming its finisher — or null while it loads (gate answer Q11: the row now, the results
   * beneath when they arrive).
   */
  full: TournamentRecord | null;
  /** Whether the standings can be drawn (`standingsState`). A failure is said, never shown as an empty sheet. */
  sheet: 'loading' | 'failed' | 'ready';
  platformTags: string[];
  onEdit: () => void;
  onUploadResults: () => void;
  onImportResults: () => void;
  onBack: () => void;
}) {
  // Paid places in order, then everyone who entered without reaching one — a null place sorts
  // before 1 otherwise, which would open the table on the bust-outs.
  const results = [...(full?.results ?? [])].sort((a, b) => {
    if (a.position === null && b.position === null) return 0;
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    return a.position - b.position;
  });

  /**
   * The field as the room actually sold it: every bullet fired, not every person who fired one.
   *
   * <b>It is a sum over what was recorded, and the card says how much of the field that was.</b>
   * `buyins` is nullable on purpose — a room that does not track re-entries has recorded neither
   * zero nor one — so a sheet with the column half filled in produces a total that is a lower
   * bound, and printing it as though it were the whole figure is the confidently-wrong number this
   * codebase spends a lot of effort not producing. The hint under it is what keeps it honest.
   *
   * This is also the number the points engine draws a multiplier band from, as `max(entries,
   * Σ buyins)` — SHRPO Event 19 is 431 players and 603 entries, and 603 is what its published ×1.8
   * comes from. An operator checking why an event landed in the band it did is looking for this
   * figure, and until now the page showed them the other one.
   */
  const recorded = results.filter((r) => r.buyins !== null && r.buyins !== undefined);
  const totalBuyins = recorded.reduce((sum, r) => sum + (r.buyins ?? 0), 0);

  /**
   * One row, computed once and rendered by both arrangements below, so the phone list and the
   * table cannot come to disagree about what an unplaced entrant, a missing card or an
   * unrecorded buy-in count looks like.
   *
   * Each result names its own finisher (the tournament's read carries it, performance rank 2), so there
   * is nothing to look up; an unclaimed place carries none and says so.
   */
  const standings = results.map((r) => {
    const player = r.player ?? undefined;
    const cardId = player?.cardId ?? null;
    const buyins = r.buyins ?? null;
    return {
      key: resultRowKey(r),
      position: r.position,
      name: standingName(r, player),
      cardId,
      buyins,
      prize: r.prize,
      /* The two demoted facts as words. Inline, a bare dash loses the column heading that gave
         it meaning, and "not recorded" is the distinction worth keeping: it is neither one
         buy-in nor none, and the buy-in total above is summed from these. */
      detail: [
        // An unclaimed place has no card of its own; the one its file carried is not shown as one.
        r.unclaimed ? 'Nobody has claimed this place' : cardId === null ? 'No card ID' : `Card ${cardId}`,
        buyins === null ? 'buy-ins not recorded' : `${buyins} buy-in${buyins === 1 ? '' : 's'}`,
      ].join(' · '),
    };
  });

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to tournaments
      </button>

      {/*
       * Two arrangements, the same rule the tournaments list follows. Three buttons come to about
       * 411px against the 366px `p-8` leaves at the 430px reference width, so below `md` only one
       * can stay and the other two move into the overflow beside it.
       *
       * Entering results is the one that stays: it is the job done standing at the desk as an
       * event finishes, and it is the same action the phone card offers, so the two agree about
       * what a tournament is *for* at that size.
       */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-ink-950">{tournament.name}</h1>
          {/* The room's own reference leads, because it is what an operator matches against
              their paperwork. The key is a GUID and says nothing to anyone, so it stays off. */}
          <p className="mt-1 text-sm text-ink-800/60">
            <span className="font-mono">{tournament.internalId}</span>
            {tournament.eventNumber ? <> · Event {tournament.eventNumber}</> : null} ·{' '}
            {tournament.date} at {tournament.time}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* T2 (docs/specs/export.md EX6): every result row, placed then unplaced, as this read orders them. */}
          <ExportControl
            availability={{ view: 'T2' }}
            target={{ view: 'T2', tournamentId: tournament.id }}
            viewLabel={tournament.name}
            subject={tournament.name}
          />
          <button
            type="button"
            onClick={onEdit}
            className="hidden items-center gap-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-white md:inline-flex"
          >
            <Edit2 className="size-4" /> Edit
          </button>
          <button
            type="button"
            onClick={onUploadResults}
            className="inline-flex items-center gap-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-white"
          >
            <Upload className="size-4" /> {tournament.entries > 0 ? 'Update results' : 'Enter results'}
          </button>
          {/* A file and a keyboard are two ways at the same job, so from `md` they sit together
              rather than one being buried in a menu. That holds where there is room for both; on a
              phone the file is the worse bet of the two, needing a CSV to hand and a four-step
              wizard, so it is the one that moves. It is in the menu, not gone. */}
          <button
            type="button"
            onClick={onImportResults}
            className="hidden items-center gap-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-white md:inline-flex"
          >
            <FileUp className="size-4" /> Import from CSV
          </button>
          {/* The same handlers as the buttons above, so the two arrangements cannot come adrift. */}
          <OverflowMenu label="More tournament actions">
            {(close) => (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    onEdit();
                  }}
                  className={overflowMenuItem}
                >
                  <Edit2 className="size-4" aria-hidden /> Edit
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    onImportResults();
                  }}
                  className={overflowMenuItem}
                >
                  <FileUp className="size-4" aria-hidden /> Import from CSV
                </button>
              </>
            )}
          </OverflowMenu>
        </div>
      </div>

      {/* Only when there is a schedule to show. A multi-day tournament with no sessions yet is
          a legitimate state, and an empty table would read as a rendering fault. */}
      {full && full.isMultiDay && full.sessions.length > 0 ? (
        <section className="mb-6 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
          <header className="border-b border-ink-800/10 px-5 py-3 text-sm font-semibold text-ink-950">
            Sessions
          </header>
          <ul className="divide-y divide-ink-800/5">
            {full.sessions.map((session) => (
              <li
                key={`${session.day}-${session.sessionName ?? ''}`}
                className="flex items-center gap-4 px-5 py-3"
              >
                <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-ink-800/60">
                  Day {session.day}
                  {session.sessionName}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink-950">{session.name}</span>
                <span className="shrink-0 text-xs text-ink-800/60">
                  {session.date} at {session.time}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mb-6 rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm">
        {/* Display only — tags are edited in the Edit modal, so there is one editor, not two. */}
        <h2 className="mb-3 text-sm font-semibold text-ink-950">Tags</h2>
        <div className="flex flex-wrap gap-1.5">
          {tournament.tags.length === 0 ? (
            <span className="text-xs text-ink-800/40">No tags yet</span>
          ) : (
            tournament.tags.map((tag) => (
              <TagPill
                key={tag}
                label={tag}
                kind={platformTags.some((p) => p.toLowerCase() === tag.toLowerCase()) ? 'platform' : 'room'}
              />
            ))
          )}
        </div>
      </section>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Buy-in" value={`$${tournament.buyIn.toLocaleString()}`} />
        {/* No guarantee is stated as such rather than shown as a dash or a zero: "nothing was
            guaranteed" is a fact about the tournament, not missing data. */}
        <StatCard
          label={tournament.guaranteeUnits ? `Guarantee (${tournament.guaranteeUnits})` : 'Guarantee'}
          value={
            tournament.guarantee === null || tournament.guarantee === undefined
              ? 'None'
              : tournament.guaranteeUnits
                ? tournament.guarantee.toLocaleString()
                : `$${tournament.guarantee.toLocaleString()}`
          }
        />
        {/* Buy-ins rather than entries, because a re-entry event's field is bullets and not
            people — and the player count it replaces is still on screen, as the length of the
            table below. Where nothing was recorded it says so instead of showing a zero: no
            room has ever run a tournament nobody entered, so a 0 here could only be read as a
            fault, and it would be the wrong one to go looking for. */}
        <StatCard
          label="Buy-ins"
          value={full === null || recorded.length === 0 ? '—' : totalBuyins.toLocaleString()}
          hint={full === null ? (sheet === 'failed' ? 'Results unavailable' : 'Loading results…') : buyinsHint(results.length, recorded.length)}
        />
        <StatCard label="Prize pool" value={`$${tournament.prizePool.toLocaleString()}`} />
        {/* Five cards into a two-column grid leaves a hole beside the last one, which reads as
            a missing card rather than as the end of the row — the same misreading the Overview
            cap was set to four to avoid. Status is the card that spans it: its value is a single
            short word, so it is the one that loses least by being wide. Dropped at `lg`, where
            five columns divide evenly and there is no hole to fill. */}
        <div className="col-span-2 lg:col-span-1">
          <StatCard label="Status" value={tournament.status} />
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        <header className="border-b border-ink-800/10 px-6 py-4 font-display font-semibold text-ink-950">
          Final standings
        </header>
        {sheet !== 'ready' ? (
          <p className="p-8 text-center text-sm text-ink-800/50" role="status">
            {sheet === 'failed'
              ? "Couldn't load this tournament's results. Go back and open it again."
              : 'Loading results…'}
          </p>
        ) : standings.length === 0 ? (
          <p className="p-8 text-center text-sm text-ink-800/50">
            No results recorded yet — this tournament is {tournament.status}.
          </p>
        ) : (
          <>
            {/*
             * Two arrangements, the same rule the rest of the section follows — and here
             * the table was not merely tight but clipped. Five columns at `px-6` spend 240px on
             * padding alone against the 366px `p-8` leaves at the 430px reference width, and this
             * section is `overflow-hidden` with no scroller, so Prize fell off the right-hand edge
             * with no gesture that could reach it.
             *
             * Below `md` the row folds to two lines rather than becoming a card. A card per
             * entrant is taller, and a sheet here is the whole field — an event paying 128
             * of 395 carries 395 rows — so height per row is the expensive dimension, and
             * cards would trade a width problem for a worse length one. Place, player and prize
             * are what the sheet is read for and hold the first line; the card number and the
             * buy-in count demote to the second.
             */}
            <ul className="divide-y divide-ink-800/5 md:hidden print:hidden">
              {standings.map((s) => (
                <li key={s.key} className="flex items-start gap-3 px-4 py-3">
                  <span className="shrink-0">
                    {s.position === null ? (
                      <span className="inline-flex size-7 items-center justify-center text-xs text-ink-800/35">
                        —
                      </span>
                    ) : (
                      <span
                        className={`inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold ${medal(s.position)}`}
                      >
                        {s.position}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink-950">{s.name}</span>
                    <span className="num mt-0.5 block text-xs text-ink-800/60">{s.detail}</span>
                  </span>
                  <span className="num shrink-0 text-sm font-medium text-ink-950">
                    ${s.prize.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>

            <table className="hidden w-full text-left text-sm md:table print:table">
              <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
                <tr>
                  <th className="px-6 py-3 font-medium">Position</th>
                  <th className="px-6 py-3 font-medium">Player</th>
                  <th className="px-6 py-3 font-medium">Card ID</th>
                  <th className="px-6 py-3 text-right font-medium">Buy-ins</th>
                  <th className="px-6 py-3 text-right font-medium">Prize</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800/5">
                {standings.map((s) => (
                  <tr key={s.key}>
                    <td className="px-6 py-3">
                      {s.position === null ? (
                        // Entered, did not reach a paid place. Shown rather than hidden: the sheet
                        // is the whole field, and a missing row would read as missing data.
                        <span className="text-xs text-ink-800/35" title="Did not reach a paid place">
                          —
                        </span>
                      ) : (
                        <span
                          className={`inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold ${medal(s.position)}`}
                        >
                          {s.position}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 font-medium text-ink-950">{s.name}</td>
                    <td className="px-6 py-3 text-ink-800/60">{s.cardId ?? '—'}</td>
                    {/* Null is "the room did not record it", which is not one buy-in and not none.
                        Shown as the same dash a missing card is, rather than as a 1 the sheet
                        never said — the total above is summed from these, so a filled-in
                        default here would inflate it. */}
                    <td className="px-6 py-3 text-right text-ink-800/60">
                      {s.buyins === null ? (
                        <span className="text-ink-800/35" title="Not recorded">
                          —
                        </span>
                      ) : (
                        s.buyins
                      )}
                    </td>
                    <td className="px-6 py-3 text-right font-medium text-ink-950">
                      ${s.prize.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}
